import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles/DocumentModal.css';
import { API_BASE_URL } from '../utils/apiConfig';
import { generateInvoicePDF } from '../utils/pdfGenerator';
import {
  formatAmountInWords as formatAmountInWordsBySystem,
  formatCurrency as formatCurrencyBySystem,
  getCurrencyCodeFromStorage,
  getCurrencyPrefixFromStorage
} from '../utils/numberingSystem';
import UnifiedPaymentModal from '../components/UnifiedPaymentModal';
import usePermissions from '../utils/usePermissions';
import InlineNotice from '../components/InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';

const PDF_REQUIRED_COLUMNS = ['sl', 'description', 'qty', 'total'];
const DEFAULT_TERMS_AND_CONDITIONS = `TERMS AND CONDITIONS

1. Price: The prices quoted are in Indian Rupees.

2. Taxes: GST as indicated above and other local levies like octroi, entry tax, etc., are not included and need to be borne by you as applicable.

3. Delivery: Delivery within 4-6 weeks from the date of receipt of your order with advance amount.

4. Payment: 100% of the total order value in advance with purchase order.

5. Installation: The installation will be done free of cost. However, all pre-installation requirements must be provided at your cost (LAN cable).

6. Warranty:
For PIXXGEN DR:
The system is guaranteed against any manufacturing defects for a period of 36 months from the date of installation or 37 months from the date of delivery, whichever is earlier, and 12 months warranty for DR battery.

7. Training: We will provide necessary training to the staff and technicians of the hospital on the operation of the system.

8. Force Majeure: Standard force majeure clause will apply.

9. Validity: This offer is valid for 30 days only.

10. Cancellation:
Any order placed pursuant hereto, forming a contract of sale, may be cancelled and terminated by the purchaser only upon written notice and upon payment to the company of 25% of the price, which the purchaser agrees and acknowledges is reasonable and fair in the circumstances. In the event the purchaser cancels the order after receipt by the company of the dispatch instructions, the purchaser shall be liable to pay to the company 40% of the price, which the purchaser agrees and acknowledges is reasonable and fair in the circumstances. The company shall also have the right to set off or adjust these charges against any monies of the purchaser lying with the company, whether relating to this transaction or not, and/or the advance paid in terms hereof. The company may also at its option cancel any order placed and accepted by the company without any liability whatsoever.

11. Subject to Chennai jurisdiction.`;

const PDF_COLUMN_DEFS = [
  { key: 'sl', label: '#', docTypes: ['quotation', 'invoice'] },
  { key: 'description', label: 'Description', docTypes: ['quotation', 'invoice'] },
  { key: 'hsn', label: 'HSN', docTypes: ['quotation', 'invoice'] },
  { key: 'rate', label: 'Rate', docTypes: ['quotation', 'invoice'] },
  { key: 'qty', label: 'Qty', docTypes: ['quotation', 'invoice'] },
  { key: 'amountExclTax', label: 'Amount (excl. Tax)', docTypes: ['quotation', 'invoice'] },
  { key: 'tax', label: 'Tax %', docTypes: ['quotation', 'invoice'] },
  { key: 'taxType', label: 'Tax Type', docTypes: ['quotation', 'invoice'] },
  { key: 'taxAmt', label: 'Tax Amount', docTypes: ['invoice'] },
  { key: 'total', label: 'Amount (incl. Tax)', docTypes: ['quotation', 'invoice'] }
];

const getPdfDocTypeKey = (docType) => (String(docType || 'invoice').toLowerCase() === 'quotation' ? 'quotation' : 'invoice');

const getDefaultPdfOptionsForDocType = (docType) => {
  const key = getPdfDocTypeKey(docType);
  const isInvoice = key === 'invoice';

  return {
    columns: {
      sl: true,
      description: true,
      hsn: false,
      rate: true,
      qty: true,
      amountExclTax: false,
      tax: isInvoice,
      taxType: isInvoice,
      taxAmt: isInvoice,
      total: true
    },
    hideTaxBreakup: false,
    showPageNumbers: false,
    selectedBankAccountIndex: 0,
    addTermsAndConditions: false,
    termsAndConditionsText: DEFAULT_TERMS_AND_CONDITIONS
  };
};

const getPdfOptionsStorageKey = (docType) => `pdf-options:${getPdfDocTypeKey(docType)}`;

const loadStoredPdfOptions = (docType) => {
  const defaults = getDefaultPdfOptionsForDocType(docType);

  try {
    const raw = localStorage.getItem(getPdfOptionsStorageKey(docType));
    if (!raw) return defaults;

    const parsed = JSON.parse(raw);
    return {
      ...defaults,
      ...parsed,
      columns: {
        ...defaults.columns,
        ...(parsed?.columns || {})
      }
    };
  } catch (error) {
    console.warn('Failed to load PDF options from local storage:', error);
    return defaults;
  }
};

const DocumentModal = ({ invoice, isOpen, onClose, currentLogo, onPaymentStatusChanged })=> {
  const invoicePerm = usePermissions('invoices');
  const quotationPerm = usePermissions('quotations');
  const docPerm = invoice?.type === 'Quotation' ? quotationPerm : invoicePerm;
  const { showInlineNotice } = useInlineNotice();
  const [orgDetails, setOrgDetails] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState('');
  const [logoData, setLogoData] = useState(currentLogo);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState(null); // null = unpaid (default), object = paid
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [documentStatus, setDocumentStatus] = useState(invoice?.status || 'Draft');
  const [confirming, setConfirming] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [selectedBankAccountIndex, setSelectedBankAccountIndex] = useState(0);
  const [pdfOptionsOpen, setPdfOptionsOpen] = useState(false);
  const [pdfOptions, setPdfOptions] = useState(() => loadStoredPdfOptions(invoice?.type));
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // Fetch organization details and logo when modal opens
  useEffect(() => {
    setDocumentStatus(invoice?.status || 'Draft');
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        setCurrentUserId(userData.id || userData._id || null);
        setUserRole(userData.role || 'user');
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    const fetchData = async () => {
      if (!isOpen || !invoice) return;
      
      try {
        setOrgLoading(true);
        setOrgError('');
        
        // Fetch organization details
        const orgResponse = await axios.get(`${API_BASE_URL}/organisations/current`);
        const orgData = orgResponse.data?.data || orgResponse.data;
        if (orgData) {
          setOrgDetails(orgData);
          if (Array.isArray(orgData?.bankAccounts) && orgData.bankAccounts.length > 0) {
            const primaryIndex = orgData.bankAccounts.findIndex((account) => account.type === 'Primary');
            setSelectedBankAccountIndex(primaryIndex >= 0 ? primaryIndex : 0);
          } else {
            setSelectedBankAccountIndex(0);
          }
        } else {
          setOrgError('Failed to load organization details');
        }

        // Fetch logo from configuration and fall back to currentLogo if needed
        try {
          const requestId = `logo-current-document-${Date.now()}-${Math.round(Math.random() * 1e9)}`;
          const logoResponse = await axios.get(`${API_BASE_URL}/logo/current`, {
            headers: {
              'X-Request-Id': requestId,
              'X-Request-Source': 'document-modal'
            }
          });
          if (logoResponse.data.success && logoResponse.data.data) {
            setLogoData(logoResponse.data.data);
          } else if (currentLogo) {
            setLogoData(currentLogo);
          }
        } catch (logoError) {
          console.error('Error fetching logo:', logoError);
          if (currentLogo) {
            setLogoData(currentLogo);
          }
          // Don't set error for logo, it's optional
        }
        
        // Fetch payment data if invoice exists
        if (invoice._id) {
          await fetchPaymentData(invoice._id);
          await fetchHistoryData(invoice._id);
        }
        
      } catch (error) {
        console.error('Error fetching organization details:', error);
        setOrgError('Failed to load organization details');
      } finally {
        setOrgLoading(false);
      }
    };

    fetchData();
  }, [isOpen, invoice, currentLogo]);

  // Fetch payment data for the invoice
  useEffect(() => {
    if (!isOpen || !invoice?.type) return;

    const storedPdfOptions = loadStoredPdfOptions(invoice.type);
    setPdfOptions(storedPdfOptions);
    if (Number.isInteger(storedPdfOptions.selectedBankAccountIndex)) {
      setSelectedBankAccountIndex(storedPdfOptions.selectedBankAccountIndex);
    }
  }, [isOpen, invoice?.type]);

  const fetchPaymentData = async (invoiceId) => {
    try {
      setPaymentLoading(true);
      setPaymentError('');
      const response = await axios.get(`${API_BASE_URL}/payments/invoice/${invoiceId}`);
      setPaymentData(response.data); // Payment exists, set it
    } catch (err) {
      // No payment found is not an error - status remains unpaid (null)
      if (err.response?.status !== 404) {
        console.error('Error fetching payment:', err);
        setPaymentError('Failed to load payment details');
      }
      setPaymentData(null); // Explicitly set to null (unpaid) as default
    } finally {
      setPaymentLoading(false);
    }
  };

  const fetchHistoryData = async (documentId) => {
    try {
      setHistoryLoading(true);
      setHistoryError('');
      const response = await axios.get(`${API_BASE_URL}/documents/${documentId}/history`);
      setHistoryData(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching history:', err);
      setHistoryError('Failed to load history');
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handlePaymentToggle = () => {
    if (!docPerm.can('payments')) return;
    // Always allow adding new payments (even if some payments exist)
    setPaymentModalOpen(true);
  };

  const handlePaymentCheckboxChange = () => {
    // Only allow payment actions for invoices, not quotations
    if (invoice.type === 'Quotation') {
      return;
    }
    handlePaymentToggle();
  };

  const handleConfirmDocument = async () => {
    if (!invoice?._id || documentStatus !== 'Draft') return;
    try {
      setConfirming(true);
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE_URL}/documents/${invoice._id}`,
        { status: 'Confirmed' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setDocumentStatus('Confirmed');
      const docNumber = invoice?.documentNumber || 'document';
      showInlineNotice(`${docNumber} confirmed successfully.`, {
        scope: 'document-modal'
      });
      await fetchHistoryData(invoice._id);
      if (onPaymentStatusChanged) {
        onPaymentStatusChanged();
      }
    } catch (err) {
      console.error('Error confirming document:', err);
      setPaymentError('Failed to confirm document');
      showInlineNotice('Failed to confirm document.', {
        scope: 'document-modal',
        type: 'error'
      });
    } finally {
      setConfirming(false);
    }
  };

  const handlePdfColumnToggle = (columnKey) => {
    if (PDF_REQUIRED_COLUMNS.includes(columnKey)) return;

    setPdfOptions((prev) => ({
      ...prev,
      columns: {
        ...prev.columns,
        [columnKey]: !prev.columns[columnKey]
      }
    }));
  };

  const handleOpenPdfOptions = () => {
    const storedPdfOptions = loadStoredPdfOptions(invoice?.type);
    setPdfOptions(storedPdfOptions);
    if (Number.isInteger(storedPdfOptions.selectedBankAccountIndex)) {
      setSelectedBankAccountIndex(storedPdfOptions.selectedBankAccountIndex);
    }
    setPdfOptionsOpen(true);
  };

  const handleGeneratePdf = async () => {
    const nextPdfOptions = {
      ...pdfOptions,
      selectedBankAccountIndex
    };

    try {
      localStorage.setItem(getPdfOptionsStorageKey(invoice?.type), JSON.stringify(nextPdfOptions));
      await generateInvoicePDF(
        invoice,
        orgDetails,
        logoData,
        orgDetails?.name,
        invoice.type,
        selectedBankAccount,
        nextPdfOptions
      );
      setPdfOptionsOpen(false);
    } catch (error) {
      console.error('Error generating PDF:', error);
      showInlineNotice('Failed to generate PDF. Please try again.', {
        scope: 'document-modal',
        type: 'error'
      });
    }
  };

  const handleDeletePayment = async (paymentId) => {
    try {
      setPaymentLoading(true);
      await axios.delete(`${API_BASE_URL}/payments/${paymentId}`);
      // Refresh payment data after deletion
      await fetchPaymentData(invoice._id);
      showInlineNotice('Payment deleted successfully.', {
        scope: 'document-modal'
      });
      await fetchHistoryData(invoice._id);
      setPaymentError('');
      // Notify parent component to refresh the documents list
      if (onPaymentStatusChanged) {
        onPaymentStatusChanged();
      }
    } catch (err) {
      console.error('Error deleting payment:', err);
      setPaymentError('Failed to delete payment');
      showInlineNotice('Failed to delete payment.', {
        scope: 'document-modal',
        type: 'error'
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handlePaymentSaved = async (newPaymentData) => {
    // Refresh payment data to get the updated list
    await fetchPaymentData(invoice._id);
    await fetchHistoryData(invoice._id);
    setPaymentError('');
    setPaymentModalOpen(false);
    // Notify parent component to refresh the documents list
    if (onPaymentStatusChanged) {
      onPaymentStatusChanged();
    }
  };

  if (!isOpen || !invoice) return null;

  const canGeneratePdf = ['Draft', 'Confirmed', 'In Progress', 'Completed'].includes(documentStatus);
  const canAddPayment = ['Confirmed', 'In Progress'].includes(documentStatus) && documentStatus !== 'Cancelled';
  const createdById = invoice.createdById || invoice.createdBy?._id || invoice.createdBy?.id || null;
  const requiresOwnerForConfirm = (quotationPerm.getAction('confirm')?.scope || '').includes('own');
  const canConfirmQuotation = invoice.type !== 'Quotation' || (
    quotationPerm.can('confirm') && (!requiresOwnerForConfirm || (currentUserId && createdById === currentUserId))
  );
  const canShowConfirm = documentStatus === 'Draft' && canConfirmQuotation;
  const canViewHistory = userRole !== 'user' || (currentUserId && createdById === currentUserId);

  const formatDate = (dateString) => {
    return dateString ? new Date(dateString).toLocaleDateString() : 'N/A';
  };

  const formatCurrency = (amount) => {
    return formatCurrencyBySystem(
      amount,
      orgDetails?.metadata?.numberingSystem || 'indian',
      { prefix: getCurrencyPrefixFromStorage() }
    );
  };

  const formatDateTime = (value) => {
    if (!value) return 'N/A';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getCustomerName = (customer) => {
    if (typeof customer === 'string') return customer;
    if (customer && customer.name) return customer.name;
    return 'Unknown Customer';
  };

  const getCreatedByLabel = () => {
    if (typeof invoice.createdBy === 'string') return invoice.createdBy;
    return invoice.createdBy?.name || invoice.createdBy?.username || 'Unknown';
  };

  const historyEntries = Array.isArray(historyData) ? historyData : [];

  const renderCustomerDetails = (customer) => {
    if (!customer || typeof customer !== 'object') {
      return <div className="customer-name">Unknown Customer</div>;
    }
    
    return (
      <div className="customer-content">
        <div className="customer-name">{customer.name}</div>
        {customer.address && (
          <div className="customer-address" style={{ whiteSpace: 'pre-line' }}>
            {customer.address}
          </div>
        )}
        <div className="customer-contact-line">
          {customer.mobile && customer.mobile !== '-' && <span>Mobile: {customer.mobile}</span>}
          {customer.email && customer.email !== '-' && (
            <>
              {customer.mobile && customer.mobile !== '-' && <span> • </span>}
              <span>Email: {customer.email}</span>
            </>
          )}
        </div>
        {customer.relationship && (
          <div className="customer-relationship">
            Relation: {customer.relationship}
          </div>
        )}
      </div>
    );
  };
  

  const calculateSubtotal = () => {
    if (!invoice.products || !Array.isArray(invoice.products)) return 0;
    return invoice.products.reduce((sum, product) => {
      const price = Number(product.price || product.rate) || 0;
      const quantity = Number(product.quantity) || 1;
      return sum + (price * quantity);
    }, 0);
  };

  const calculateTotalTax = () => {
    if (!invoice.products || !Array.isArray(invoice.products)) return 0;
    return invoice.products.reduce((sum, product) => {
      const price = Number(product.price || product.rate) || 0;
      const tax = Number(product.tax) || 0;
      const quantity = Number(product.quantity) || 1;
      const taxAmount = (price * quantity * tax) / 100;
      return sum + taxAmount;
    }, 0);
  };

  const getTaxCategory = (taxType) => {
    const normalized = String(taxType || '').toUpperCase();
    if (normalized.includes('IGST')) {
      return 'IGST';
    }
    return 'CGST/SGST';
  };

  const calculateTaxBreakdown = () => {
    if (!invoice.products || !Array.isArray(invoice.products)) {
      return { IGST: 0, 'CGST/SGST': 0 };
    }
    return invoice.products.reduce(
      (totals, product) => {
        const price = Number(product.price || product.rate) || 0;
        const tax = Number(product.tax) || 0;
        const quantity = Number(product.quantity) || 1;
        const taxAmount = (price * quantity * tax) / 100;
        const category = getTaxCategory(product.taxType);
        totals[category] = (totals[category] || 0) + taxAmount;
        return totals;
      },
      { IGST: 0, 'CGST/SGST': 0 }
    );
  };

  const bankAccounts = Array.isArray(orgDetails?.bankAccounts) ? orgDetails.bankAccounts : [];
  const selectedBankAccount = bankAccounts[selectedBankAccountIndex] || null;
  const pdfDocTypeKey = getPdfDocTypeKey(invoice?.type);
  const pdfOptionHelpText = pdfDocTypeKey === 'quotation'
    ? 'Choose the layout for this quotation PDF. Your last used quotation settings will be remembered on this device.'
    : 'Choose the layout for this invoice PDF. Your last used invoice settings will be remembered on this device.';
  const availablePdfColumns = PDF_COLUMN_DEFS.filter((column) => column.docTypes.includes(pdfDocTypeKey));
  const requiredPdfColumns = availablePdfColumns.filter((column) => PDF_REQUIRED_COLUMNS.includes(column.key));
  const optionalPdfColumns = availablePdfColumns.filter((column) => !PDF_REQUIRED_COLUMNS.includes(column.key));
  const contactNumbers = Array.isArray(orgDetails?.contactNumbers)
    ? orgDetails.contactNumbers.filter(Boolean)
    : (orgDetails?.phone ? [orgDetails.phone] : []);
  const websites = Array.isArray(orgDetails?.websites) ? orgDetails.websites.filter(Boolean) : [];
  const subtotal = calculateSubtotal();
  const totalTax = calculateTotalTax();
  const taxBreakdown = calculateTaxBreakdown();
  const totalBeforeRoundOff = subtotal + totalTax;
  const invoiceTotal = Number(invoice.total);
  const grandTotal = Number.isFinite(invoiceTotal) ? invoiceTotal : totalBeforeRoundOff;
  const roundOffTotalsEnabled = Boolean(orgDetails?.metadata?.roundOffTotals);
  const roundingOffAmount = grandTotal - totalBeforeRoundOff;
  const amountInWords = formatAmountInWordsBySystem(
    grandTotal,
    orgDetails?.metadata?.numberingSystem || 'indian',
    { currencyCode: orgDetails?.metadata?.currency?.code || getCurrencyCodeFromStorage() }
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{invoice.type || 'Invoice'} Details</h2>
          <div className="header-right">
            <div className="header-invoice-info">
              <div className="header-info-item">
                <span className="header-label">Document Type:</span>
                <span className="header-value">{invoice.type || 'Invoice'}</span>
              </div>
              <div className="header-info-item">
                <span className="header-label">Document No:</span>
                <span className="header-value">{invoice.documentNumber || 'N/A'}</span>
              </div>
              <div className="header-info-item">
                <span className="header-label">Date:</span>
                <span className="header-value">{formatDate(invoice.createdAt)}</span>
              </div>
              <div className="header-info-item">
                <span className="header-label">Created By:</span>
                <span className="header-value">{typeof invoice.createdBy === 'string' ? invoice.createdBy : (invoice.createdBy?.name || invoice.createdBy?.username || 'Unknown')}</span>
              </div>
              <div className="header-info-item">
                <span className="header-label">Status:</span>
                <span className="header-value">{documentStatus}</span>
              </div>
            </div>
            <button className="modal-close" onClick={onClose}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
        
        <div className="modal-body">
          <InlineNotice scope="document-modal" />
          {/* Organization Details Section */}
          <div className="org-details-section">
            {orgLoading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <span>Loading...</span>
              </div>
            ) : orgError ? (
              <div className="error-state">
                <span>{orgError}</span>
              </div>
            ) : orgDetails ? (
              <div className="org-info-card">
                <div className="org-header-with-logo">
                  {logoData && (
                    <img 
                      src={logoData.fullUrl || logoData.logoPath} 
                      alt="Company Logo" 
                      className="modal-org-logo"
                    />
                  )}
                  <div className="org-text-content">
                    <div className="org-name">{orgDetails.name}</div>
                    <div className="org-details">
                      {orgDetails.address && <div className="org-address">{orgDetails.address}</div>}
                      <div className="org-contact">
                        {contactNumbers.length > 0 && <span>Phone: {contactNumbers.join(', ')}</span>}
                        {orgDetails.gst && contactNumbers.length > 0 && <span> • </span>}
                        {orgDetails.gst && <span>GST: {orgDetails.gst}</span>}
                      </div>
                      {websites.length > 0 && (
                        <div className="org-contact">
                          <span>Website: {websites.join(', ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="bill-to-section">
            <h3 className="bill-to-title">Bill To</h3>
            <div className="bill-to-info-card">
              {renderCustomerDetails(invoice.customer)}
            </div>
          </div>

          {invoice.products && invoice.products.length > 0 && (
            <div className="products-section">
              <h3>Products</h3>
              <div className="products-table-container">
                <table className="modal-products-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>HSN</th>
                      <th>Rate</th>
                      <th>Qty</th>
                      <th>Amount (excl. Tax)</th>
                      <th>Tax %</th>
                      <th>Tax Type</th>
                      <th>Tax Amount</th>
                      <th>Amount (incl. Tax)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.products.map((product, index) => {
                      const price = Number(product.price || product.rate) || 0;
                      const tax = Number(product.tax) || 0;
                      const quantity = Number(product.quantity) || 1;
                      const amountExcludingTax = price * quantity;
                      const taxAmount = (amountExcludingTax * tax) / 100;
                      const total = amountExcludingTax + taxAmount;
                      const hsnValue = product.hsncode ?? product.hsnCode ?? product.hsn ?? product.productId?.hsncode ?? product.productId?.hsnCode ?? '-';
                      
                      return (
                        <tr key={index}>
                          <td className="product-description-cell" style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                            {product.description || product.name || product.slno || 'N/A'}
                          </td>
                          <td className="product-hsn-cell">{hsnValue}</td>
                          <td className="product-numeric-cell">{formatCurrency(price)}</td>
                          <td className="product-numeric-cell">{quantity}</td>
                          <td className="product-numeric-cell">{formatCurrency(amountExcludingTax)}</td>
                          <td className="product-numeric-cell">{tax}%</td>
                          <td className="product-tax-type-cell">{product.taxType || 'N/A'}</td>
                          <td className="product-numeric-cell">{formatCurrency(taxAmount)}</td>
                          <td className="product-numeric-cell">{formatCurrency(total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payment Status Section (Invoices only) */}
          {invoice.type !== 'Quotation' && (
            <div style={{
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <h3 style={{
                  margin: '0',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1f2937'
                }}>
                  {invoice.type === 'Invoice' ? 'Credit Status' : 'Payment Status'}
                </h3>
                <button
                  onClick={() => setPaymentModalOpen(true)}
                  disabled={paymentLoading || !canAddPayment || (paymentData && paymentData.remainingBalance <= 0)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#2563eb',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: (paymentLoading || !canAddPayment || (paymentData && paymentData.remainingBalance <= 0)) ? 'not-allowed' : 'pointer',
                    opacity: (paymentLoading || !canAddPayment || (paymentData && paymentData.remainingBalance <= 0)) ? 0.6 : 1
                  }}
                >
                  {paymentData && paymentData.remainingBalance <= 0 
                    ? (invoice.type === 'Invoice' ? 'Fully Credited' : 'Paid')
                    : (invoice.type === 'Invoice' ? 'Add Credit' : 'Add Payment')}
                </button>
              </div>

              {paymentData && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px',
                    padding: '12px',
                    backgroundColor: '#fff',
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Invoice Total</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>{formatCurrency(paymentData.invoiceTotal)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Total Paid</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: '#16a34a' }}>{formatCurrency(paymentData.totalPaid)}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Remaining</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: paymentData.remainingBalance > 0 ? '#dc2626' : '#16a34a' }}>
                        {formatCurrency(paymentData.remainingBalance)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {paymentData && paymentData.payments && paymentData.payments.length > 0 ? (
                <div>
                  <div style={{
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '8px'
                  }}>
                    Payment History ({paymentData.payments.length})
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {paymentData.payments.map((payment, index) => (
                      <div
                        key={payment._id}
                        style={{
                          backgroundColor: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          padding: '12px',
                          marginBottom: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '4px'
                          }}>
                            <span style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#16a34a'
                            }}>
                              {formatCurrency(payment.amount)}
                            </span>
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              backgroundColor: '#f3f4f6',
                              borderRadius: '3px',
                              color: '#6b7280'
                            }}>
                              {payment.paymentMode}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '2px' }}>
                            {new Date(payment.paymentDate).toLocaleDateString('en-IN')}
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '2px' }}>
                            {payment.paymentReference}
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>
                            Added by {payment.createdBy?.name || 'Unknown'} on {new Date(payment.createdAt).toLocaleDateString('en-IN')} at {new Date(payment.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            if (window.confirm('Are you sure you want to delete this payment?')) {
                              handleDeletePayment(payment._id);
                            }
                          }}
                          disabled={paymentLoading}
                          style={{
                            padding: '6px 8px',
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '4px',
                            fontSize: '11px',
                            cursor: paymentLoading ? 'not-allowed' : 'pointer',
                            opacity: paymentLoading ? 0.6 : 1
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p style={{
                  margin: '0',
                  fontSize: '13px',
                  color: '#6b7280',
                  textAlign: 'center',
                  padding: '12px'
                }}>
                  No payments recorded yet
                </p>
              )}

              {paymentError && (
                <div style={{
                  marginTop: '12px',
                  padding: '8px 12px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #fecaca',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#dc2626'
                }}>
                  {paymentError}
                </div>
              )}
            </div>
          )}

          {canViewHistory && (
            <div style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '20px',
              backgroundColor: '#fff'
            }}>
              <button
                type="button"
                onClick={() => setHistoryExpanded(prev => !prev)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#1f2937'
                }}
              >
                <span>History</span>
                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                  {historyExpanded ? 'Hide' : 'Show'}
                </span>
              </button>

              {historyExpanded && (
                <div style={{ marginTop: '12px' }}>
                  {historyLoading ? (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>Loading history...</div>
                  ) : historyError ? (
                    <div style={{ fontSize: '13px', color: '#dc2626' }}>{historyError}</div>
                  ) : historyEntries.length === 0 ? (
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>No history yet.</div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                          <tr style={{ textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>
                            <th style={{ padding: '8px 6px', color: '#374151' }}>Action</th>
                            <th style={{ padding: '8px 6px', color: '#374151' }}>DateTime</th>
                            <th style={{ padding: '8px 6px', color: '#374151' }}>Author</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyEntries.map((entry) => (
                            <tr key={entry._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '8px 6px', color: '#1f2937' }}>{entry.action}</td>
                              <td style={{ padding: '8px 6px', color: '#374151' }}>{formatDateTime(entry.createdAt)}</td>
                              <td style={{ padding: '8px 6px', color: '#374151' }}>{entry.createdBy?.name || 'Unknown'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="totals-section">
            {roundOffTotalsEnabled ? (
              <>
                <div className="totals-row">
                  <span className="totals-label">Total (excl. Tax):</span>
                  <span className="totals-value">{formatCurrency(subtotal)}</span>
                </div>
                {taxBreakdown.IGST > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">IGST:</span>
                    <span className="totals-value">{formatCurrency(taxBreakdown.IGST)}</span>
                  </div>
                )}
                {taxBreakdown['CGST/SGST'] > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">CGST/SGST:</span>
                    <span className="totals-value">{formatCurrency(taxBreakdown['CGST/SGST'])}</span>
                  </div>
                )}
                <div className="totals-row">
                  <span className="totals-label">Rounding off:</span>
                  <span className="totals-value">{formatCurrency(roundingOffAmount)}</span>
                </div>
                <div className="totals-row total-row">
                  <span className="totals-label">Grand Total:</span>
                  <span className="totals-value">{formatCurrency(grandTotal)}</span>
                </div>
              </>
            ) : (
              <>
                <div className="totals-row">
                  <span className="totals-label">Subtotal:</span>
                  <span className="totals-value">{formatCurrency(subtotal)}</span>
                </div>
                {taxBreakdown.IGST > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">IGST:</span>
                    <span className="totals-value">{formatCurrency(taxBreakdown.IGST)}</span>
                  </div>
                )}
                {taxBreakdown['CGST/SGST'] > 0 && (
                  <div className="totals-row">
                    <span className="totals-label">CGST/SGST:</span>
                    <span className="totals-value">{formatCurrency(taxBreakdown['CGST/SGST'])}</span>
                  </div>
                )}
                <div className="totals-row total-row">
                  <span className="totals-label">Grand Total:</span>
                  <span className="totals-value">{formatCurrency(grandTotal)}</span>
                </div>
              </>
            )}
            <div className="amount-in-words">({amountInWords})</div>
          </div>

          <div className="modal-actions">
            {canShowConfirm && (
              <button
                className="modal-btn modal-btn-pdf"
                onClick={handleConfirmDocument}
                disabled={confirming}
              >
                {confirming ? 'Confirming...' : 'Confirm'}
              </button>
            )}
            <button className="modal-btn modal-btn-pdf" onClick={handleOpenPdfOptions} disabled={!canGeneratePdf}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14,2H6a2,2,0,0,0-2,2V20a2,2,0,0,0,2,2H18a2,2,0,0,0,2-2V8Z"/>
                <polyline points="14,2 14,8 20,8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10,9 9,9 8,9"/>
              </svg>
              PDF
            </button>
          </div>
        </div>
      </div>
      
      {pdfOptionsOpen && (
        <div className="modal-overlay pdf-options-overlay" onClick={() => setPdfOptionsOpen(false)}>
          <div className="modal-content pdf-options-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{invoice.type || 'Document'} PDF Options</h2>
              <button className="modal-close" onClick={() => setPdfOptionsOpen(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p className="pdf-options-description">{pdfOptionHelpText}</p>

              {bankAccounts.length > 1 && (
                <div className="bank-account-selector pdf-options-bank-selector">
                  <label htmlFor="bankAccountSelect">Bank Account for this PDF</label>
                  <select
                    id="bankAccountSelect"
                    value={selectedBankAccountIndex}
                    onChange={(e) => setSelectedBankAccountIndex(Number(e.target.value))}
                    className="bank-account-select"
                  >
                    {bankAccounts.map((account, index) => {
                      const name = account.bankName || 'Bank';
                      const accountNumber = account.accountNumber || '';
                      const lastFour = accountNumber ? accountNumber.slice(-4) : 'XXXX';
                      const typeLabel = account.type || 'Primary';
                      return (
                        <option key={`${name}-${index}`} value={index}>
                          {`${typeLabel} - ${name} (${lastFour})`}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              <div className="pdf-options-card">
                <label className="pdf-option-toggle-row">
                  <input
                    type="checkbox"
                    checked={!!pdfOptions.hideTaxBreakup}
                    onChange={() => setPdfOptions((prev) => ({
                      ...prev,
                      hideTaxBreakup: !prev.hideTaxBreakup
                    }))}
                  />
                  <span>Hide tax breakup</span>
                </label>
                <label className="pdf-option-toggle-row" style={{ marginTop: '0.6rem' }}>
                  <input
                    type="checkbox"
                    checked={!!pdfOptions.showPageNumbers}
                    onChange={() => setPdfOptions((prev) => ({
                      ...prev,
                      showPageNumbers: !prev.showPageNumbers
                    }))}
                  />
                  <span>Show page numbers (e.g. 1/3)</span>
                </label>
              </div>

              <div className="pdf-options-card">
                <div className="pdf-options-card-title">Visible columns</div>
                <div className="pdf-options-group">
                  <div className="pdf-options-group-title">Required fields</div>
                  <div className="pdf-options-grid">
                    {requiredPdfColumns.map((column) => {
                      const checked = !!pdfOptions?.columns?.[column.key];
                      return (
                        <label key={column.key} className="pdf-option-chip required">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled
                            onChange={() => handlePdfColumnToggle(column.key)}
                          />
                          <span>{column.label}</span>
                          <em>Required</em>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div className="pdf-options-group">
                  <div className="pdf-options-group-title">Optional fields</div>
                  <div className="pdf-options-grid">
                    {optionalPdfColumns.map((column) => {
                      const checked = !!pdfOptions?.columns?.[column.key];
                      return (
                        <label key={column.key} className="pdf-option-chip">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => handlePdfColumnToggle(column.key)}
                          />
                          <span>{column.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="pdf-options-card">
                <label className="pdf-option-toggle-row">
                  <input
                    type="checkbox"
                    checked={!!pdfOptions.addTermsAndConditions}
                    onChange={() => setPdfOptions((prev) => ({
                      ...prev,
                      addTermsAndConditions: !prev.addTermsAndConditions
                    }))}
                  />
                  <span>Add T&amp;C page</span>
                </label>

                {pdfOptions.addTermsAndConditions && (
                  <div className="pdf-terms-editor-wrap">
                    <label htmlFor="pdfTermsAndConditions" className="pdf-terms-editor-label">
                      Terms and Conditions
                    </label>
                    <textarea
                      id="pdfTermsAndConditions"
                      className="pdf-terms-editor"
                      value={pdfOptions.termsAndConditionsText || ''}
                      onChange={(e) => setPdfOptions((prev) => ({
                        ...prev,
                        termsAndConditionsText: e.target.value
                      }))}
                      rows={12}
                    />
                    <p className="pdf-terms-editor-help">
                      This text will be added as an appendix after the main document and will automatically continue to additional pages when needed.
                    </p>
                    <p className="pdf-terms-editor-help">
                      Supported formatting tags: <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code>, <code>&lt;u&gt;</code>, <code>&lt;br&gt;</code>, <code>&lt;p&gt;</code>.
                      Example: <code>&lt;b&gt;1. Price:&lt;/b&gt; The prices quoted are in Indian Rupees.&lt;br&gt;&lt;i&gt;Subject to stock availability.&lt;/i&gt;</code>
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="modal-btn"
                onClick={() => setPdfOptionsOpen(false)}
                style={{ backgroundColor: '#e5e7eb', color: '#1f2937' }}
              >
                Cancel
              </button>
              <button className="modal-btn modal-btn-pdf" onClick={handleGeneratePdf} disabled={!canGeneratePdf}>
                Generate PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <UnifiedPaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        paymentType="invoice"
        entity={invoice._id}
        onPaymentSaved={handlePaymentSaved}
      />
    </div>
  );
};

export default DocumentModal;